import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAdminTransactions, AdminTransaction } from '@/hooks/admin/useAdminTransactions';
import {
    ArrowLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';

const TRANSACTION_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'purchase', label: 'Purchase' },
    { value: 'usage', label: 'Usage' },
    { value: 'refund', label: 'Refund' },
    { value: 'manual_add', label: 'Manual Add' },
    { value: 'manual_deduct', label: 'Manual Deduct' },
];

function TransactionRow({ transaction }: { transaction: AdminTransaction }) {
    const isCredit = ['purchase', 'manual_add', 'refund'].includes(transaction.type);
    const date = new Date(transaction.createdAt);

    return (
        <tr className="border-b border-gray-200 dark:border-gray-700">
            <td className="px-4 py-3 text-gray-900 dark:text-white">
                {transaction.userEmail}
            </td>
            <td className="px-4 py-3">
                <span className="rounded-sm bg-gray-100 px-2 py-1 text-xs font-medium dark:bg-gray-700">
                    {transaction.typeLabel}
                </span>
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                {transaction.description}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                {date.toLocaleDateString()} {date.toLocaleTimeString()}
            </td>
            <td className="px-4 py-3">
                {transaction.adminId && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        Admin #{transaction.adminId}
                    </span>
                )}
            </td>
            <td className="px-4 py-3 text-right">
                <span
                    className={`text-lg font-semibold ${
                        isCredit ? 'text-green-600' : 'text-red-600'
                    }`}
                >
                    {isCredit ? '+' : '-'}{transaction.amount}
                </span>
            </td>
        </tr>
    );
}

export function AdminTransactionsPage() {
    usePageTitle('Admin - Transactions');

    const [page, setPage] = useState(1);
    const [typeFilter, setTypeFilter] = useState('');
    const limit = 20;

    const { data, isLoading } = useAdminTransactions(
        page,
        limit,
        undefined,
        typeFilter || undefined
    );

    const handleTypeChange = (value: string) => {
        setTypeFilter(value);
        setPage(1);
    };

    if (isLoading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center gap-4">
                <Link
                    to="/admin"
                    className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Admin
                </Link>
            </div>

            <PageHeader title="Transactions" />

            {/* Filter */}
            <div className="flex items-center gap-4">
                <div className="w-48">
                    <Select
                        label="Filter by type"
                        value={typeFilter}
                        onChange={handleTypeChange}
                        options={TRANSACTION_TYPE_OPTIONS}
                    />
                </div>
            </div>

            {/* Transactions Table */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-secondary">
                {data?.transactions && data.transactions.length > 0 ? (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            User
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Description
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Date
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Admin
                                        </th>
                                        <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300">
                                            Amount
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.transactions.map((transaction) => (
                                        <TransactionRow
                                            key={transaction.id}
                                            transaction={transaction}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Showing {(page - 1) * limit + 1} to{' '}
                                {Math.min(page * limit, data.total)} of {data.total} transactions
                            </p>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                    Previous
                                </Button>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Page {page} of {data.totalPages}
                                </span>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= data.totalPages}
                                >
                                    Next
                                    <ChevronRightIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                        <ClockIcon className="mb-2 h-8 w-8" />
                        <p>No transactions found</p>
                    </div>
                )}
            </div>
        </>
    );
}
